import { useCallback, useEffect, useState } from 'react';
import { KotLine, KotTicket, TableLock, TableOrder, Transaction, TransactionItem } from '../types';
import * as db from '../services/db';
import { opUpsert, opDelete, opSet, COLLECTION_CHANGED } from '../services/ops';
import { getDeviceId, getDeviceName } from '../services/license';
import { usePos } from '../context/PosContext';

// A table lock older than this (without a heartbeat refresh) is treated as free,
// so a terminal that crashed or walked out of Wi-Fi range never wedges a table.
// The holder refreshes well within this window (see LOCK_HEARTBEAT_MS in the UI).
const LOCK_STALE_MS = 90_000;

// Shared state + operations for the restaurant module (Tables, Captain App,
// KOT Report, Kitchen Display). Backed by IndexedDB via db.ts and kept reactive
// with React state.
//
// Multi-terminal: every change is emitted as a granular operation (see
// services/ops.ts) — a single table order / KOT is upserted or deleted by id,
// never the whole array. So two captains working different tables merge cleanly
// instead of overwriting each other, and a KOT fired on a captain tablet shows
// up live on the kitchen display and the billing counter. The COLLECTION_CHANGED
// effect below reloads state when those ops arrive from another device.
//
// Settlement reuses PosContext.recordSale so a table bill flows through the same
// sequential (server-leased) bill numbering, stock decrement and movement log as
// the regular Bill screen.
export function useRestaurant() {
  const pos = usePos();
  const [tables, setTables] = useState<string[]>([]);
  const [orders, setOrders] = useState<TableOrder[]>([]);
  const [kots, setKots] = useState<KotTicket[]>([]);
  const [locks, setLocks] = useState<TableLock[]>([]);

  // Storage is async; load the table state once on mount.
  useEffect(() => {
    let alive = true;
    Promise.all([db.getTables(), db.getTableOrders(), db.getKots(), db.getTableLocks()]).then(
      ([t, o, k, l]) => {
        if (!alive) return;
        setTables(t);
        setOrders(o);
        setKots(k);
        setLocks(l);
      }
    );
    return () => {
      alive = false;
    };
  }, []);

  // Live sync: when a table/KOT op arrives from another terminal, applyOp fires
  // COLLECTION_CHANGED and we reload just that collection.
  useEffect(() => {
    const reload = (e: Event) => {
      const c = (e as CustomEvent<{ collection: string }>).detail?.collection;
      if (c === 'tables') db.getTables().then(setTables);
      else if (c === 'tableOrders') db.getTableOrders().then(setOrders);
      else if (c === 'kots') db.getKots().then(setKots);
      else if (c === 'tableLocks') db.getTableLocks().then(setLocks);
    };
    window.addEventListener(COLLECTION_CHANGED, reload);
    return () => window.removeEventListener(COLLECTION_CHANGED, reload);
  }, []);

  // ---- Granular persistence: one op per changed row, mirrored into state ----
  const upsertOrder = useCallback((o: TableOrder) => {
    opUpsert('tableOrders', o.id, o);
    setOrders((prev) => {
      const i = prev.findIndex((x) => x.id === o.id);
      if (i >= 0) {
        const copy = prev.slice();
        copy[i] = o;
        return copy;
      }
      return [...prev, o];
    });
  }, []);

  const removeOrder = useCallback((id: string) => {
    opDelete('tableOrders', id);
    setOrders((prev) => prev.filter((o) => o.id !== id));
  }, []);

  const upsertKot = useCallback((k: KotTicket) => {
    opUpsert('kots', k.id, k);
    setKots((prev) => {
      const i = prev.findIndex((x) => x.id === k.id);
      if (i >= 0) {
        const copy = prev.slice();
        copy[i] = k;
        return copy;
      }
      return [...prev, k];
    });
  }, []);

  // ---- Table locks ----
  // The lock currently held on a table by ANOTHER terminal (fresh, not this
  // device), or null if the table is free to open. Stale locks are ignored.
  const lockHolder = useCallback(
    (tableName: string): TableLock | null => {
      const l = locks.find((x) => x.tableName === tableName);
      if (!l || l.deviceId === getDeviceId()) return null;
      if (Date.now() - new Date(l.at).getTime() > LOCK_STALE_MS) return null;
      return l;
    },
    [locks]
  );

  // Try to take the lock for editing. Returns false (and changes nothing) if
  // another terminal holds it; otherwise claims/refreshes it for this device.
  const acquireLock = useCallback(
    (tableName: string): boolean => {
      if (lockHolder(tableName)) return false;
      const lock: TableLock = {
        tableName,
        deviceId: getDeviceId(),
        deviceName: getDeviceName() || undefined,
        at: new Date().toISOString(),
      };
      opUpsert('tableLocks', tableName, lock);
      setLocks((prev) => [...prev.filter((l) => l.tableName !== tableName), lock]);
      return true;
    },
    [lockHolder]
  );

  // Heartbeat: keep this device's lock fresh while the panel stays open.
  const refreshLock = useCallback((tableName: string) => {
    const lock: TableLock = {
      tableName,
      deviceId: getDeviceId(),
      deviceName: getDeviceName() || undefined,
      at: new Date().toISOString(),
    };
    opUpsert('tableLocks', tableName, lock);
    setLocks((prev) => [...prev.filter((l) => l.tableName !== tableName), lock]);
  }, []);

  // Release the lock. Callers only release a table they successfully acquired
  // (see acquireLock's return value), so this is stable (no `locks` dependency)
  // and safe to use in a heartbeat effect's cleanup.
  const releaseLock = useCallback((tableName: string) => {
    opDelete('tableLocks', tableName);
    setLocks((prev) => prev.filter((x) => x.tableName !== tableName));
  }, []);

  // ---- Table list ----
  // The table list is a small shared blob; a whole-list set op is fine here
  // (editing the floor plan on two devices at once is rare).
  const addTable = useCallback((name: string) => {
    const clean = name.trim();
    if (!clean) return;
    setTables((prev) => {
      if (prev.includes(clean)) return prev;
      const next = [...prev, clean];
      opSet('tables', next);
      return next;
    });
  }, []);

  const removeTable = useCallback((name: string) => {
    setTables((prev) => {
      const next = prev.filter((t) => t !== name);
      opSet('tables', next);
      return next;
    });
  }, []);

  // ---- Running orders ----
  const runningOrder = useCallback(
    (tableName: string): TableOrder | undefined =>
      orders.find((o) => o.tableName === tableName && o.status === 'running'),
    [orders]
  );

  // Fire a new round of items to the kitchen. Creates a KOT ticket and appends
  // the lines onto the table's running order (opening one if needed).
  const sendKot = useCallback(
    async (
      tableName: string,
      lines: KotLine[],
      opts: {
        waiter?: string;
        customerName?: string;
        customerId?: string;
        covers?: number;
        discountAmount?: number;
      } = {}
    ): Promise<KotTicket | null> => {
      if (lines.length === 0) return null;

      const amount = lines.reduce((s, l) => s + l.unitPrice * l.quantity, 0);
      const kot: KotTicket = {
        id: db.newId(),
        kotNo: await db.nextKotNo(),
        tableName,
        waiter: opts.waiter,
        customerName: opts.customerName,
        lines,
        amount,
        status: 'active',
        timestamp: new Date().toISOString(),
      };

      const newItems: TransactionItem[] = lines.map((l) => ({
        productId: l.productId,
        name: l.name,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        taxRatePercent:
          pos.products.find((p) => p.id === l.productId)?.taxRatePercent ?? pos.config.taxRatePercent,
        total: l.unitPrice * l.quantity,
      }));

      const existing = orders.find((o) => o.tableName === tableName && o.status === 'running');
      if (existing) {
        upsertOrder({
          ...existing,
          items: mergeItems(existing.items, newItems),
          kotIds: [...existing.kotIds, kot.id],
          waiter: opts.waiter ?? existing.waiter,
          customerName: opts.customerName ?? existing.customerName,
          customerId: opts.customerId ?? existing.customerId,
          covers: opts.covers ?? existing.covers,
          discountAmount: opts.discountAmount ?? existing.discountAmount,
        });
      } else {
        upsertOrder({
          id: db.newId(),
          tableName,
          customerId: opts.customerId,
          customerName: opts.customerName,
          waiter: opts.waiter,
          covers: opts.covers,
          discountAmount: opts.discountAmount,
          items: newItems,
          kotIds: [kot.id],
          status: 'running',
          openedAt: new Date().toISOString(),
        });
      }

      upsertKot(kot);
      return kot;
    },
    [orders, pos.products, pos.config.taxRatePercent, upsertOrder, upsertKot]
  );

  // "15 — Table Settings / J - Table Book Without KOT": occupy a table with an
  // empty running order so it shows booked before any items are fired.
  const bookTable = useCallback(
    (tableName: string) => {
      if (orders.some((o) => o.tableName === tableName && o.status === 'running')) return;
      upsertOrder({
        id: db.newId(),
        tableName,
        items: [],
        kotIds: [],
        status: 'running',
        openedAt: new Date().toISOString(),
      });
    },
    [orders, upsertOrder]
  );

  // Free a booked-but-empty table again.
  const unbookTable = useCallback(
    (tableName: string) => {
      const order = orders.find((o) => o.tableName === tableName && o.status === 'running');
      if (!order || order.items.length > 0) return;
      removeOrder(order.id);
    },
    [orders, removeOrder]
  );

  // Update header meta (customer / waiter / covers / discount) on a running
  // table order. No-op if the table has no open order yet.
  const setOrderMeta = useCallback(
    (
      tableName: string,
      patch: Partial<Pick<TableOrder, 'waiter' | 'customerId' | 'customerName' | 'covers' | 'discountAmount'>>
    ) => {
      const order = orders.find((o) => o.tableName === tableName && o.status === 'running');
      if (!order) return;
      upsertOrder({ ...order, ...patch });
    },
    [orders, upsertOrder]
  );

  // Edit a sent line's quantity (0 removes it). If the order ends up empty the
  // table is freed. Historical KOTs are left untouched (the running order total
  // is what gets billed).
  const updateOrderItemQty = useCallback(
    (tableName: string, productId: string, unitPrice: number, newQty: number) => {
      const order = orders.find((o) => o.tableName === tableName && o.status === 'running');
      if (!order) return;
      const items = order.items
        .map((i) =>
          i.productId === productId && i.unitPrice === unitPrice
            ? { ...i, quantity: newQty, total: unitPrice * newQty }
            : i
        )
        .filter((i) => i.quantity > 0);
      if (items.length === 0) removeOrder(order.id);
      else upsertOrder({ ...order, items });
    },
    [orders, removeOrder, upsertOrder]
  );

  // Cancel a KOT: mark it cancelled and pull its lines back out of the table's
  // running order. A cancelled KOT stays in the report (struck through).
  const cancelKot = useCallback(
    (kotId: string) => {
      const kot = kots.find((k) => k.id === kotId);
      if (!kot || kot.status !== 'active') return;

      upsertKot({ ...kot, status: 'cancelled' });

      const order = orders.find((o) => o.kotIds.includes(kotId) && o.status === 'running');
      if (order) {
        const reduced = removeItems(order.items, kot.lines);
        // If nothing remains, drop the empty running order entirely.
        if (reduced.length === 0) removeOrder(order.id);
        else upsertOrder({ ...order, items: reduced });
      }
    },
    [kots, orders, upsertKot, removeOrder, upsertOrder]
  );

  // Kitchen Display: mark a KOT prepared (or clear it). Uses `preparedAt`, so the
  // billing lifecycle in `status` is untouched.
  const markKotPrepared = useCallback(
    (kotId: string, prepared: boolean) => {
      const kot = kots.find((k) => k.id === kotId);
      if (!kot) return;
      upsertKot({ ...kot, preparedAt: prepared ? new Date().toISOString() : undefined });
    },
    [kots, upsertKot]
  );

  // Settle a table into a completed sale and free the table. `discountAmount`
  // and `covers` override whatever is stored on the order (so the panel can
  // pass the latest edits without a separate persist round-trip).
  // Build a draft (unrecorded) sale for a running table, so the caller can take
  // it through the shared PaymentModal (split tender / loyalty) before recording.
  const buildSettleDraft = useCallback(
    async (
      tableName: string,
      opts: { discountAmount?: number; covers?: number } = {}
    ): Promise<Transaction | null> => {
      const order = orders.find((o) => o.tableName === tableName && o.status === 'running');
      if (!order || order.items.length === 0) return null;

      const subtotal = order.items.reduce((s, i) => s + i.total, 0);
      const tax = order.items.reduce((s, i) => s + i.total * (i.taxRatePercent / 100), 0);
      const discount = Math.max(0, opts.discountAmount ?? order.discountAmount ?? 0);
      const covers = opts.covers ?? order.covers;
      const total = Math.max(0, subtotal + tax - discount);

      return {
        id: db.newId(),
        billNo: await db.peekNextBillNo(), // preview; recordSale assigns the committed number
        customerId: order.customerId,
        customerName: order.customerName,
        items: order.items,
        subtotal,
        tax,
        discount,
        total,
        paymentMethod: 'cash',
        table: tableName,
        captain: order.waiter,
        covers,
        orderType: 'dinein',
        timestamp: new Date().toISOString(),
        status: 'pending',
      };
    },
    [orders]
  );

  // After a draft has been recorded as a sale, mark the table settled and its
  // KOTs billed.
  const finalizeSettle = useCallback(
    (tableName: string, sale: Transaction) => {
      const order = orders.find((o) => o.tableName === tableName && o.status === 'running');
      if (!order) return;
      upsertOrder({
        ...order,
        status: 'settled',
        settledAt: new Date().toISOString(),
        billNo: sale.billNo,
      });
      kots.forEach((k) => {
        if (order.kotIds.includes(k.id) && k.status === 'active') upsertKot({ ...k, status: 'billed' });
      });
    },
    [orders, kots, upsertOrder, upsertKot]
  );

  const settleTable = useCallback(
    async (
      tableName: string,
      paymentMethod: Transaction['paymentMethod'],
      opts: { discountAmount?: number; covers?: number } = {}
    ): Promise<Transaction | null> => {
      const draft = await buildSettleDraft(tableName, opts);
      if (!draft) return null;
      const sale = await pos.recordSale({ ...draft, paymentMethod, status: 'completed' });
      finalizeSettle(tableName, sale);
      return sale;
    },
    [buildSettleDraft, finalizeSettle, pos]
  );

  // ---- Merge: move one running table's order into another. The source table
  // is freed; its KOTs are reassigned to the target table.
  const mergeTables = useCallback(
    (fromTable: string, toTable: string) => {
      if (fromTable === toTable) return;
      const from = orders.find((o) => o.tableName === fromTable && o.status === 'running');
      if (!from) return;
      const to = orders.find((o) => o.tableName === toTable && o.status === 'running');

      if (to) {
        removeOrder(from.id);
        upsertOrder({
          ...to,
          items: mergeItems(to.items, from.items),
          kotIds: [...to.kotIds, ...from.kotIds],
          covers: (to.covers ?? 0) + (from.covers ?? 0) || undefined,
          customerId: to.customerId ?? from.customerId,
          customerName: to.customerName ?? from.customerName,
          waiter: to.waiter ?? from.waiter,
        });
      } else {
        // Target is free: just relocate the source order onto it.
        upsertOrder({ ...from, tableName: toTable });
      }
      kots.forEach((k) => {
        if (from.kotIds.includes(k.id)) upsertKot({ ...k, tableName: toTable });
      });
    },
    [orders, kots, removeOrder, upsertOrder, upsertKot]
  );

  // ---- Split: move selected item quantities off one table onto another
  // (existing running order or a fresh one). Items remaining on the source stay
  // put; if the source is emptied it is freed.
  const splitTable = useCallback(
    (
      fromTable: string,
      toTable: string,
      moves: { productId: string; unitPrice: number; quantity: number }[]
    ) => {
      if (fromTable === toTable) return;
      const from = orders.find((o) => o.tableName === fromTable && o.status === 'running');
      if (!from || moves.length === 0) return;

      const remaining = from.items.map((i) => ({ ...i }));
      const moved: TransactionItem[] = [];
      moves.forEach((mv) => {
        const src = remaining.find((i) => i.productId === mv.productId && i.unitPrice === mv.unitPrice);
        if (!src) return;
        const qty = Math.min(mv.quantity, src.quantity);
        if (qty <= 0) return;
        moved.push({ ...src, quantity: qty, total: src.unitPrice * qty });
        src.quantity -= qty;
        src.total = src.unitPrice * src.quantity;
      });
      if (moved.length === 0) return;
      const newRemaining = remaining.filter((i) => i.quantity > 0);

      // Update / free the source.
      if (newRemaining.length === 0) removeOrder(from.id);
      else upsertOrder({ ...from, items: newRemaining });

      // Land the moved items on the target (merge into a running order, or open one).
      const to = orders.find((o) => o.tableName === toTable && o.status === 'running' && o.id !== from.id);
      if (to) {
        upsertOrder({ ...to, items: mergeItems(to.items, moved) });
      } else {
        upsertOrder({
          id: db.newId(),
          tableName: toTable,
          customerId: from.customerId,
          customerName: from.customerName,
          waiter: from.waiter,
          items: moved,
          kotIds: [...from.kotIds],
          status: 'running',
          openedAt: new Date().toISOString(),
        });
      }
    },
    [orders, removeOrder, upsertOrder]
  );

  // ---- Swap: exchange the running orders of two tables (either side may be
  // free, in which case it degrades to a plain transfer). KOTs follow their
  // order so the KOT Report / Kitchen Display show the new table names.
  const swapTables = useCallback(
    (tableA: string, tableB: string) => {
      if (tableA === tableB) return;
      const a = orders.find((o) => o.tableName === tableA && o.status === 'running');
      const b = orders.find((o) => o.tableName === tableB && o.status === 'running');
      if (!a && !b) return;
      if (a) upsertOrder({ ...a, tableName: tableB });
      if (b) upsertOrder({ ...b, tableName: tableA });
      kots.forEach((k) => {
        if (a && a.kotIds.includes(k.id)) upsertKot({ ...k, tableName: tableB });
        else if (b && b.kotIds.includes(k.id)) upsertKot({ ...k, tableName: tableA });
      });
    },
    [orders, kots, upsertOrder, upsertKot]
  );

  // ---- Transfer: move a running order onto a FREE table (guests changed
  // seats). Refuses if the target is occupied — use mergeTables for that.
  const transferTable = useCallback(
    (fromTable: string, toTable: string): boolean => {
      if (fromTable === toTable) return false;
      if (orders.some((o) => o.tableName === toTable && o.status === 'running')) return false;
      swapTables(fromTable, toTable);
      return true;
    },
    [orders, swapTables]
  );

  // ---- Void: cancel every active KOT on a table and free it without billing
  // (walk-out / wrong table). Cancelled KOTs stay in the KOT Report.
  const voidTable = useCallback(
    (tableName: string) => {
      const order = orders.find((o) => o.tableName === tableName && o.status === 'running');
      if (!order) return;
      kots.forEach((k) => {
        if (order.kotIds.includes(k.id) && k.status === 'active') upsertKot({ ...k, status: 'cancelled' });
      });
      removeOrder(order.id);
    },
    [orders, kots, upsertKot, removeOrder]
  );

  // ---- Bill merge: one draft sale covering several running tables (a party
  // spread over T1+T2 paying together). Items are combined; discounts stored on
  // each order are summed unless overridden. Tables are NOT touched until
  // finalizeSettleMany runs after payment.
  const buildMergedSettleDraft = useCallback(
    async (
      tableNames: string[],
      opts: { discountAmount?: number } = {}
    ): Promise<Transaction | null> => {
      const group = tableNames
        .map((t) => orders.find((o) => o.tableName === t && o.status === 'running'))
        .filter((o): o is TableOrder => !!o && o.items.length > 0);
      if (group.length === 0) return null;

      const items = group.reduce<TransactionItem[]>((acc, o) => mergeItems(acc, o.items), []);
      const subtotal = items.reduce((s, i) => s + i.total, 0);
      const tax = items.reduce((s, i) => s + i.total * (i.taxRatePercent / 100), 0);
      const discount = Math.max(
        0,
        opts.discountAmount ?? group.reduce((s, o) => s + (o.discountAmount ?? 0), 0)
      );
      const covers = group.reduce((s, o) => s + (o.covers ?? 0), 0) || undefined;
      const total = Math.max(0, subtotal + tax - discount);
      const first = group.find((o) => o.customerId) ?? group[0];

      return {
        id: db.newId(),
        billNo: await db.peekNextBillNo(),
        customerId: first.customerId,
        customerName: first.customerName,
        items,
        subtotal,
        tax,
        discount,
        total,
        paymentMethod: 'cash',
        table: group.map((o) => o.tableName).join('+'),
        captain: group.find((o) => o.waiter)?.waiter,
        covers,
        orderType: 'dinein',
        timestamp: new Date().toISOString(),
        status: 'pending',
      };
    },
    [orders]
  );

  // Mark every table in a merged bill settled (and its KOTs billed).
  const finalizeSettleMany = useCallback(
    (tableNames: string[], sale: Transaction) => {
      tableNames.forEach((t) => finalizeSettle(t, sale));
    },
    [finalizeSettle]
  );

  // ---- Split bill: bill only part of a table now (one guest pays for their
  // share) and leave the rest running. Returns a draft for the chosen
  // quantities; call finalizeSplitSettle after payment to pull them off the
  // table. Historical KOTs are untouched (they mark billed when the table is
  // finally cleared).
  const buildSplitSettleDraft = useCallback(
    async (
      tableName: string,
      picks: { productId: string; unitPrice: number; quantity: number }[],
      opts: { discountAmount?: number; label?: string } = {}
    ): Promise<Transaction | null> => {
      const order = orders.find((o) => o.tableName === tableName && o.status === 'running');
      if (!order) return null;
      const items: TransactionItem[] = [];
      picks.forEach((pk) => {
        const src = order.items.find((i) => i.productId === pk.productId && i.unitPrice === pk.unitPrice);
        if (!src) return;
        const qty = round3(Math.min(pk.quantity, src.quantity));
        if (qty <= 0) return;
        items.push({ ...src, quantity: qty, total: round2(src.unitPrice * qty) });
      });
      if (items.length === 0) return null;
      const subtotal = items.reduce((s, i) => s + i.total, 0);
      const tax = items.reduce((s, i) => s + i.total * (i.taxRatePercent / 100), 0);
      const discount = Math.max(0, opts.discountAmount ?? 0);
      return {
        id: db.newId(),
        billNo: await db.peekNextBillNo(),
        customerId: order.customerId,
        customerName: order.customerName,
        items,
        subtotal,
        tax,
        discount,
        total: Math.max(0, subtotal + tax - discount),
        paymentMethod: 'cash',
        table: opts.label ? `${tableName} ${opts.label}` : tableName,
        captain: order.waiter,
        covers: order.covers,
        orderType: 'dinein',
        timestamp: new Date().toISOString(),
        status: 'pending',
      };
    },
    [orders]
  );

  // Equal split into N bills: the item quantities for share `k` (1-based) of
  // `n`. Each share takes 1/(shares left) of what is still on the table
  // (rounded to 3 dp); the last share takes everything that remains so
  // rounding never leaves a sliver behind.
  const equalSharePicks = useCallback(
    (tableName: string, k: number, n: number) => {
      const order = orders.find((o) => o.tableName === tableName && o.status === 'running');
      if (!order || n < 2 || k < 1 || k > n) return [];
      const sharesLeft = n - k + 1;
      return order.items.map((i) => ({
        productId: i.productId,
        unitPrice: i.unitPrice,
        quantity: k === n ? i.quantity : round3(i.quantity / sharesLeft),
      }));
    },
    [orders]
  );

  // After a split bill is paid, remove the billed quantities from the running
  // order. If that empties the table it is settled like a normal bill.
  const finalizeSplitSettle = useCallback(
    (tableName: string, sale: Transaction) => {
      const order = orders.find((o) => o.tableName === tableName && o.status === 'running');
      if (!order) return;
      const remaining = removeItems(
        order.items,
        sale.items.map((i) => ({ productId: i.productId, name: i.name, quantity: i.quantity, unitPrice: i.unitPrice }))
      );
      if (remaining.length === 0) {
        finalizeSettle(tableName, sale);
      } else {
        upsertOrder({ ...order, items: remaining });
      }
    },
    [orders, finalizeSettle, upsertOrder]
  );

  return {
    tables,
    orders,
    kots,
    locks,
    lockHolder,
    acquireLock,
    refreshLock,
    releaseLock,
    addTable,
    removeTable,
    bookTable,
    unbookTable,
    runningOrder,
    sendKot,
    cancelKot,
    markKotPrepared,
    settleTable,
    buildSettleDraft,
    finalizeSettle,
    mergeTables,
    splitTable,
    swapTables,
    transferTable,
    voidTable,
    buildMergedSettleDraft,
    finalizeSettleMany,
    buildSplitSettleDraft,
    finalizeSplitSettle,
    equalSharePicks,
    setOrderMeta,
    updateOrderItemQty,
  };
}

// Merge new lines into an item list, summing quantity for matching products.
function mergeItems(existing: TransactionItem[], incoming: TransactionItem[]): TransactionItem[] {
  const out = existing.map((i) => ({ ...i }));
  for (const inc of incoming) {
    const match = out.find((i) => i.productId === inc.productId && i.unitPrice === inc.unitPrice);
    if (match) {
      match.quantity += inc.quantity;
      match.total = match.unitPrice * match.quantity;
    } else {
      out.push({ ...inc });
    }
  }
  return out;
}

// Subtract KOT lines back out of an item list (used when cancelling a KOT).
function removeItems(items: TransactionItem[], lines: KotLine[]): TransactionItem[] {
  const out = items.map((i) => ({ ...i }));
  for (const line of lines) {
    const match = out.find((i) => i.productId === line.productId && i.unitPrice === line.unitPrice);
    if (match) {
      match.quantity = round3(match.quantity - line.quantity);
      match.total = round2(match.unitPrice * match.quantity);
    }
  }
  return out.filter((i) => i.quantity > 0);
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const round3 = (n: number) => Math.round(n * 1000) / 1000;
